import React, { useState, useEffect, useRef } from "react";
import { ThreadSearch, GlobalMessageSearch } from "./ChatSearch";
import { sendNotification } from "./notify";
import axios from "axios";
import { db } from "../firebase";
import {
  collection, addDoc, onSnapshot, query,
  orderBy, serverTimestamp, updateDoc, doc, deleteDoc
} from "firebase/firestore";

const CLOUD_NAME = "dscxfsvrf";
const UPLOAD_PRESET = "ficsori-portal";
// Team members loaded dynamically from Firestore
const MENTION_REGEX = /@(\w[\w\s]*?)(?=\s|$|[^a-zA-Z\s])/g;

const S = {
  card:{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,overflow:"hidden"},
  btn:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  btnPrimary:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
  input:{width:"100%",padding:"6px 9px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",background:"#fff",boxSizing:"border-box"},
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"2px 7px",borderRadius:20,background:bg,color:c,fontWeight:500}),
};

function formatTime(ts){
  if(!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const diff = now - d;
  if(diff < 60000) return "just now";
  if(diff < 3600000) return Math.floor(diff/60000)+"m ago";
  if(diff < 86400000) return d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
}

function renderAttachment(msg){
  if(msg.attachmentType === "image"){
    return (
      <div>
        <img src={msg.attachmentUrl} alt={msg.attachmentName}
          style={{maxWidth:"100%",maxHeight:200,borderRadius:8,display:"block",marginBottom:4}}/>
        <a href={msg.attachmentUrl} target="_blank" rel="noreferrer"
          style={{fontSize:11,color:"inherit",opacity:0.7}}>↓ {msg.attachmentName}</a>
      </div>
    );
  }
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:"rgba(0,0,0,0.08)",borderRadius:6}}>
      <span style={{fontSize:18}}>📎</span>
      <div>
        <div style={{fontSize:12,fontWeight:500}}>{msg.attachmentName}</div>
        <a href={msg.attachmentUrl} target="_blank" rel="noreferrer"
          style={{fontSize:11,color:"inherit",opacity:0.7}}>↓ Download</a>
      </div>
    </div>
  );
}

function renderMessage(text){
  const parts = text.split(/(@\w[\w\s]*?)(?=\s|$)/g);
  return parts.map((part,i)=>{
    if(part.startsWith("@")){
      return <span key={i} style={{background:"#E6F1FB",color:"#185FA5",borderRadius:4,padding:"0 3px",fontWeight:500}}>{part}</span>;
    }
    if(part.startsWith("#task:")){
      const taskTitle = part.replace("#task:","");
      return <span key={i} style={{background:"#EEEDFE",color:"#534AB7",borderRadius:4,padding:"0 3px",fontWeight:500,cursor:"pointer"}}>📋 {taskTitle}</span>;
    }
    return <span key={i}>{part}</span>;
  });
}

function Avatar({name, size=28}){
  const words=(name||"").trim().split(" ");
  const initials=((words[0]?.[0]||"")+(words[1]?.[0]||"")).toUpperCase();
  const colors=[["#fdf0f0","#C0111F"],["#E6F1FB","#185FA5"],["#E1F5EE","#0F6E56"],["#EEEDFE","#534AB7"]];
  const [bg,c]=colors[(name||"").length%colors.length];
  return <div style={{width:size,height:size,borderRadius:"50%",background:bg,color:c,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.36,fontWeight:500,flexShrink:0}}>{initials}</div>;
}

function MessageBubble({msg, isOwn, showAvatar, onEdit, onDelete, isAdmin, editingId, editText, setEditText, onSaveEdit, onCancelEdit}){
  return (
    <div style={{display:"flex",gap:8,marginBottom:4,flexDirection:isOwn?"row-reverse":"row",alignItems:"flex-end"}}>
      {showAvatar
        ?<Avatar name={msg.senderName} size={24}/>
        :<div style={{width:24,flexShrink:0}}/>
      }
      <div style={{maxWidth:"70%"}}>
        {showAvatar&&!isOwn&&<div style={{fontSize:10,color:"#888",marginBottom:2,marginLeft:2}}>{msg.senderName}</div>}
        {editingId===msg.firestoreId ? (
          <div style={{background:"#f5f5f0",borderRadius:12,padding:"6px 8px",minWidth:200}}>
            <textarea
              style={{width:"100%",padding:"6px 8px",fontSize:13,border:"0.5px solid #ccc",borderRadius:6,fontFamily:"sans-serif",resize:"none",boxSizing:"border-box"}}
              value={editText} onChange={e=>setEditText(e.target.value)}
              rows={2} autoFocus
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();onSaveEdit(msg);}if(e.key==="Escape")onCancelEdit();}}
            />
            <div style={{display:"flex",gap:6,marginTop:4}}>
              <button onClick={()=>onSaveEdit(msg)} style={{fontSize:11,padding:"2px 8px",borderRadius:6,border:"none",background:"#C0111F",color:"#fff",cursor:"pointer"}}>Save</button>
              <button onClick={onCancelEdit} style={{fontSize:11,padding:"2px 8px",borderRadius:6,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{position:"relative"}} className="msg-bubble-wrap">
            <div style={{position:"relative",display:"inline-block"}} className="msg-bubble-wrap">
              <div style={{
                background:isOwn?"#C0111F":"#f5f5f0",
                color:isOwn?"#fff":"#1a1a18",
                padding:"8px 12px",borderRadius:isOwn?"12px 12px 2px 12px":"12px 12px 12px 2px",
                fontSize:13,lineHeight:1.5,wordBreak:"break-word"
              }}>
                {msg.isAttachment ? renderAttachment(msg) : renderMessage(msg.text)}
              </div>
              <span
                title="Copy message"
                className="msg-copy-btn"
                onClick={(e)=>{ e.stopPropagation(); navigator.clipboard.writeText(msg.text||""); const t=e.currentTarget; t.textContent="✓"; setTimeout(()=>t.textContent="⧉",1500); }}
                style={{position:"absolute",top:4,right:4,cursor:"pointer",fontSize:11,padding:"1px 5px",background:"rgba(255,255,255,0.85)",borderRadius:4,border:"0.5px solid #ccc",opacity:0.55,transition:"opacity 0.2s",fontWeight:500}}
              >⧉</span>
            </div>
            {msg.edited&&<div style={{fontSize:10,color:"#aaa",marginTop:1,textAlign:isOwn?"right":"left",fontStyle:"italic"}}>(edited)</div>}
            <div style={{display:"flex",alignItems:"center",gap:4,marginTop:2,justifyContent:isOwn?"flex-end":"flex-start"}}>
              <span style={{fontSize:10,color:"#aaa"}}>{formatTime(msg.createdAt)}</span>
              {isOwn&&(
                msg.readBy&&msg.readBy.length>0
                  ? <span style={{fontSize:10,color:"#3B6D11"}} title={"Read by: "+msg.readBy.join(", ")}>✓✓</span>
                  : <span style={{fontSize:10,color:"#aaa"}} title="Sent">✓</span>
              )}
            </div>
            {(isOwn||isAdmin)&&(
              <div style={{position:"absolute",top:-8,[isOwn?"left":"right"]:-60,display:"flex",gap:3,opacity:0.55,transition:"opacity 0.2s"}}
                className="msg-actions"
                onMouseEnter={e=>e.currentTarget.style.opacity=1}
                onMouseLeave={e=>e.currentTarget.style.opacity=0}>
                {(isOwn||isAdmin)&&<span title="Edit" onClick={()=>onEdit(msg)} style={{cursor:"pointer",fontSize:14,padding:"2px 5px",background:"#fff",borderRadius:6,border:"0.5px solid #e0dfd8",boxShadow:"0 1px 4px rgba(0,0,0,0.1)"}}>✏</span>}
                <span title="Copy" onClick={(e)=>{ e.stopPropagation(); navigator.clipboard.writeText(msg.text||""); e.currentTarget.textContent="✓"; setTimeout(()=>e.currentTarget.textContent="⧉",1200); }} style={{cursor:"pointer",fontSize:11,padding:"2px 6px",background:"rgba(255,255,255,0.9)",borderRadius:4,border:"0.5px solid #e0dfd8",boxShadow:"0 1px 3px rgba(0,0,0,0.1)",fontFamily:"monospace",fontWeight:500}}>⧉</span>
                {(isOwn||isAdmin)&&<span title="Delete" onClick={()=>onDelete(msg)} style={{cursor:"pointer",fontSize:14,padding:"2px 5px",background:"#fff",borderRadius:6,border:"0.5px solid #e0dfd8",boxShadow:"0 1px 4px rgba(0,0,0,0.1)",color:"#C0111F"}}>🗑</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TeamChat({ currentUserName, currentUserEmail, tasks=[] }){
  const [teamMembers, setTeamMembers] = useState(["Frank Ficsori"]);

  useEffect(()=>{
    const unsub = onSnapshot(collection(db,"team"), snap=>{
      const names = snap.docs.map(d=>d.data().name).filter(Boolean).sort();
      setTeamMembers(["Frank Ficsori", ...names]);
    });
    return ()=>unsub();
  },[]);

  const [channels] = useState([
    {id:"general", name:"General", icon:"💬", desc:"Team announcements and general discussion"},
    {id:"bookkeeping", name:"Bookkeeping", icon:"📊", desc:"Bookkeeping work and client discussions"},
    {id:"tax", name:"Tax", icon:"📋", desc:"Tax season coordination"},
    {id:"tasks", name:"Tasks", icon:"✅", desc:"Task-related discussions"},
  ]);
  const [activeChannel, setActiveChannel] = useState("general");
  const [messages, setMessages] = useState({});
  const [input, setInput] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [showTaskRef, setShowTaskRef] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [unread, setUnread] = useState({});
  const bottomRef = useRef();
  const scrollContainerRef = useRef();
  const inputRef = useRef();
  const fileRef = useRef();
  const searchRef = useRef();
  const [uploading, setUploading] = useState(false);
  const messageRefs = useRef({});

  const jumpToMessage = (id) => {
    const el = messageRefs.current[id];
    if(el){ el.scrollIntoView({behavior:"smooth",block:"center"}); el.style.background="#FFF3CD"; setTimeout(()=>el.style.background="transparent",2000); }
  };
  const myName = currentUserName || "Frank Ficsori";

  useEffect(()=>{
    const unsubs = channels.map(ch=>{
      const q = query(collection(db,"teamChat_"+ch.id), orderBy("createdAt","asc"));
      return onSnapshot(q, snap=>{
        const msgs = snap.docs.map(d=>({...d.data(), firestoreId:d.id}));
        setMessages(prev=>({...prev,[ch.id]:msgs}));
        if(ch.id !== activeChannel){
          const myLastSeen = parseInt(localStorage.getItem("chat_seen_"+ch.id)||"0");
          const newMsgs = msgs.filter(m=>{
            const ts = m.createdAt?.toDate?.()?.getTime()||0;
            return ts > myLastSeen && m.senderName !== myName;
          });
          if(newMsgs.length>0) setUnread(prev=>({...prev,[ch.id]:(prev[ch.id]||0)+newMsgs.length}));
        }
      });
    });
    return ()=>unsubs.forEach(u=>u());
  },[]);

  useEffect(()=>{
    const container = scrollContainerRef.current;
    if(container){
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
      if(isNearBottom) bottomRef.current?.scrollIntoView({behavior:"smooth"});
    }
    localStorage.setItem("chat_seen_"+activeChannel, Date.now().toString());
    setUnread(prev=>({...prev,[activeChannel]:0}));
  },[activeChannel, messages[activeChannel]?.length]);
  // Scroll to bottom when switching channels
  useEffect(()=>{
    setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:"auto"}), 50);
  },[activeChannel]);


  const handleInput = (e) => {
    const val = e.target.value;
    setInput(val);
    const lastWord = val.split(" ").pop();
    if(lastWord.startsWith("@")){
      setMentionQuery(lastWord.slice(1).toLowerCase());
      setShowMentions(true);
      setShowTaskRef(false);
    } else if(lastWord === "#"){
      setShowTaskRef(true);
      setShowMentions(false);
    } else {
      setShowMentions(false);
      setShowTaskRef(false);
    }
  };

  const insertMention = (name) => {
    const words = input.split(" ");
    words[words.length-1] = "@"+name+" ";
    setInput(words.join(" "));
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const insertTaskRef = (task) => {
    const words = input.split(" ");
    words[words.length-1] = "#task:"+task.title+" ";
    setInput(words.join(" "));
    setShowTaskRef(false);
    inputRef.current?.focus();
  };

  const uploadFile = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    formData.append("folder", "ficsori-portal/team-chat");
    try {
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`, formData
      );
      const isImage = file.type.startsWith("image/");
      const attachmentText = isImage
        ? `[image:${file.name}:${res.data.secure_url}]`
        : `[file:${file.name}:${res.data.secure_url}]`;
      // Send as message
      await addDoc(collection(db,"teamChat_"+activeChannel),{
        text: attachmentText,
        senderName: myName,
        createdAt: serverTimestamp(),
        mentions: [],
        channel: activeChannel,
        isAttachment: true,
        attachmentType: isImage ? "image" : "file",
        attachmentName: file.name,
        attachmentUrl: res.data.secure_url,
      });
    } catch(e){ alert("Upload failed: "+e.message); }
    setUploading(false);
  };

  const handleFileAttach = (e) => {
    if(e.target.files[0]) uploadFile(e.target.files[0]);
    e.target.value = "";
  };

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const deleteMessage = async (msg) => {
    if(!window.confirm("Delete this message?")) return;
    try{ await deleteDoc(doc(db,"teamChat_"+activeChannel, msg.firestoreId)); }
    catch(e){ alert("Failed to delete: "+e.message); }
  };

  const startEdit = (msg) => {
    setEditingId(msg.firestoreId);
    setEditText(msg.text);
  };

  const saveEdit = async (msg) => {
    if(!editText.trim()) return;
    try{
      await updateDoc(doc(db,"teamChat_"+activeChannel, msg.firestoreId),{
        text: editText.trim(),
        edited: true,
        editedAt: new Date().toISOString(),
      });
      setEditingId(null);
      setEditText("");
    }catch(e){ alert("Failed to edit: "+e.message); }
  };

  const sendMessage = async () => {
    if(!input.trim()) return;
    const mentions = [];
    let match;
    const re = /@([\w\s]+?)(?=\s|$)/g;
    while((match=re.exec(input))!==null) mentions.push(match[1].trim());

    try{
      await addDoc(collection(db,"teamChat_"+activeChannel),{
        text: input.trim(),
        senderName: myName,
        createdAt: serverTimestamp(),
        mentions,
        channel: activeChannel,
      });
      setInput("");
      // Notify the other person
      const senderIsFrank = currentUserEmail === "frank@ficsoriaccounting.com";
      if(senderIsFrank){
        // Frank sent — notify David
        sendNotification("task_assigned",{
          recipientEmail: "lookup@placeholder.com",
          recipientName: "David Warner",
          senderName: "Frank Ficsori",
          clientName: "",
          details: `#${activeChannel}: ${input.trim().slice(0,200)}`,
          portalUrl: "https://portal.ficsoriaccounting.com/#team-chat",
        });
      } else if(!senderIsFrank){
        // Team member sent — notify Frank
        sendNotification("task_assigned",{
          recipientEmail: "frank@ficsoriaccounting.com",
          recipientName: "Frank Ficsori",
          senderName: myName,
          clientName: "",
          details: `#${activeChannel}: ${input.trim().slice(0,200)}`,
          portalUrl: "https://portal.ficsoriaccounting.com/#team-chat",
        });
      }
    }catch(e){alert("Failed to send: "+e.message);}
  };

  const handleKeyDown = (e) => {
    if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); sendMessage(); }
    if(e.key==="Escape"){ setShowMentions(false); setShowTaskRef(false); }
  };

  const channelMessages = messages[activeChannel]||[];
  const filteredMentions = teamMembers.filter(u=>u.toLowerCase().includes(mentionQuery)&&u!==myName);

  // Check if I'm mentioned in any message
  const myMentions = Object.values(messages).flat().filter(m=>
    m.mentions?.includes(myName) && m.senderName!==myName
  ).length;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{fontSize:14,fontWeight:500}}>Team Chat</div>
          {myMentions>0&&<span style={S.badge("#E6F1FB","#185FA5")}>@{myMentions} mention{myMentions!==1?"s":""}</span>}
        </div>
        <div style={{fontSize:12,color:"#888"}}>Internal team messaging · Not visible to clients</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"200px 1fr",gap:0,border:"0.5px solid #e0dfd8",borderRadius:12,overflow:"hidden",height:"calc(100vh - 160px)"}}>

        {/* Sidebar — channels */}
        <div style={{background:"#1a1a18",display:"flex",flexDirection:"column"}}>
          <div style={{padding:"14px 12px 8px",fontSize:10,color:"#666",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:500}}>Channels</div>
          {channels.map(ch=>{
            const isActive = activeChannel===ch.id;
            const unreadCount = unread[ch.id]||0;
            return (
              <div key={ch.id} onClick={()=>setActiveChannel(ch.id)} style={{
                display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"8px 12px",cursor:"pointer",borderRadius:6,margin:"1px 6px",
                background:isActive?"#333":"transparent",
                color:isActive?"#fff":"#999",
              }}
                onMouseEnter={e=>!isActive&&(e.currentTarget.style.background="#222")}
                onMouseLeave={e=>!isActive&&(e.currentTarget.style.background="transparent")}
              >
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:13}}>
                  <span style={{fontSize:14}}>{ch.icon}</span>
                  {ch.name}
                </div>
                {unreadCount>0&&<span style={{background:"#C0111F",color:"#fff",fontSize:10,padding:"1px 5px",borderRadius:10,fontWeight:500}}>{unreadCount}</span>}
              </div>
            );
          })}

          <div style={{padding:"14px 12px 8px",marginTop:8,fontSize:10,color:"#666",textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:500,borderTop:"0.5px solid #333"}}>Team</div>
          {teamMembers.map(u=>(
            <div key={u} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",fontSize:12,color:"#888"}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:"#3B6D11",flexShrink:0}}/>
              {u}
            </div>
          ))}
        </div>

        {/* Main chat area */}
        <div style={{display:"flex",flexDirection:"column",background:"#fff",overflow:"hidden",minHeight:0}}>
          {/* Channel header */}
          <div style={{padding:"12px 16px",borderBottom:"0.5px solid #e0dfd8",display:"flex",alignItems:"center",gap:8,justifyContent:"space-between"}}>
            <span style={{fontSize:16}}>{channels.find(c=>c.id===activeChannel)?.icon}</span>
            <div>
              <div style={{fontSize:13,fontWeight:500}}>{channels.find(c=>c.id===activeChannel)?.name}</div>
              <div style={{fontSize:11,color:"#888"}}>{channels.find(c=>c.id===activeChannel)?.desc}</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <ThreadSearch messages={channelMessages} onJumpTo={jumpToMessage} searchRef={searchRef}/>
              <GlobalMessageSearch isAdmin={myName==="Frank Ficsori"} currentUserName={myName}/>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollContainerRef} style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",userSelect:"text",WebkitUserSelect:"text",MozUserSelect:"text",cursor:"auto"}}>
            {channelMessages.length===0&&(
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",color:"#aaa"}}>
                <div style={{fontSize:32,marginBottom:8}}>{channels.find(c=>c.id===activeChannel)?.icon}</div>
                <div style={{fontSize:13,fontWeight:500,marginBottom:4}}>Start the conversation</div>
                <div style={{fontSize:12}}>This is the beginning of #{channels.find(c=>c.id===activeChannel)?.name.toLowerCase()}</div>
              </div>
            )}
            {channelMessages.map((msg,i)=>{
              const isOwn = msg.senderName===myName;
              const prevMsg = channelMessages[i-1];
              const showAvatar = !prevMsg || prevMsg.senderName!==msg.senderName;
              const isMentioned = msg.mentions?.includes(myName);
              return (
                <div key={msg.firestoreId} ref={el=>messageRefs.current[msg.firestoreId]=el} style={{background:isMentioned&&!isOwn?"#FFF8E6":"transparent",borderRadius:6,margin:"0 -8px",padding:"0 8px",transition:"background 0.5s"}}>
                  <MessageBubble
                    msg={msg} isOwn={isOwn} showAvatar={showAvatar}
                    isAdmin={myName==="Frank Ficsori"}
                    onEdit={startEdit} onDelete={deleteMessage}
                    editingId={editingId} editText={editText}
                    setEditText={setEditText}
                    onSaveEdit={saveEdit}
                    onCancelEdit={()=>{setEditingId(null);setEditText("");}}
                  />
                </div>
              );
            })}
            <div ref={bottomRef}/>
          </div>

          {/* Input area */}
          <div style={{padding:"12px 16px",borderTop:"0.5px solid #e0dfd8",position:"relative"}}>
            {/* Mention suggestions */}
            {showMentions&&filteredMentions.length>0&&(
              <div style={{position:"absolute",bottom:"100%",left:16,background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",overflow:"hidden",zIndex:10}}>
                <div style={{padding:"6px 10px",fontSize:10,color:"#888",background:"#f5f5f0",borderBottom:"0.5px solid #e0dfd8"}}>Mention a team member</div>
                {filteredMentions.map(u=>(
                  <div key={u} onClick={()=>insertMention(u)} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",cursor:"pointer",fontSize:12}}
                    onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <Avatar name={u} size={20}/>
                    {u}
                  </div>
                ))}
              </div>
            )}
            {/* Task reference suggestions */}
            {showTaskRef&&tasks.length>0&&(
              <div style={{position:"absolute",bottom:"100%",left:16,background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",overflow:"hidden",zIndex:10,maxHeight:200,overflowY:"auto",minWidth:280}}>
                <div style={{padding:"6px 10px",fontSize:10,color:"#888",background:"#f5f5f0",borderBottom:"0.5px solid #e0dfd8"}}>Reference a task</div>
                {tasks.slice(0,10).map(t=>(
                  <div key={t.firestoreId} onClick={()=>insertTaskRef(t)} style={{padding:"8px 12px",cursor:"pointer",fontSize:12,borderBottom:"0.5px solid #f5f5f0"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    📋 {t.title} {t.client&&<span style={{color:"#888",fontSize:11}}>— {t.client}</span>}
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
              <div style={{flex:1,position:"relative"}}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={handleInput}
                  onKeyDown={handleKeyDown}
                  placeholder={`Message #${channels.find(c=>c.id===activeChannel)?.name.toLowerCase()} · @mention · # for tasks`}
                  style={{...S.input,resize:"none",minHeight:38,maxHeight:120,overflowY:"auto",paddingRight:8,lineHeight:1.5}}
                  rows={1}
                />
              </div>
              <button
                style={{height:38,width:38,borderRadius:8,border:"0.5px solid #ccc",background:uploading?"#f5f5f0":"#fff",cursor:uploading?"wait":"pointer",fontSize:18,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}
                onClick={()=>!uploading&&fileRef.current.click()}
                title="Attach file or image"
                disabled={uploading}
              >
                {uploading?"⏳":"📎"}
              </button>
              <input ref={fileRef} type="file" style={{display:"none"}} onChange={handleFileAttach}/>
              <button style={{...S.btnPrimary,height:38,paddingLeft:14,paddingRight:14,flexShrink:0}} onClick={sendMessage} disabled={!input.trim()}>
                Send ↑
              </button>
            </div>
            <div style={{fontSize:10,color:"#aaa",marginTop:4}}>Enter to send · Shift+Enter for new line · @ to mention · # for tasks · 📎 to attach files</div>
          </div>
        </div>
      </div>
    </div>
  );
}
